<template>
    <b-card class="card-accent-secondary" header-tag="header" footer-tag="footer" no-body>
        <template v-slot:header>
            <i class="fa-regular fa-files-medical"></i> <strong>General History</strong>
            <div class="card-header-actions">
                <b-link class="card-header-action btn-minimize" @click="visible = !visible">
                    <i class="fa-regular" :class="visible ? 'fa-square-caret-up' : 'fa-square-caret-down'"></i>
                </b-link>
            </div>
        </template>
        <b-collapse v-model="visible">
        <b-overlay 
            :show="$store.getters['loading/isLoading']('history')"
            rounded="sm"
            variant="light"
            spinner-variant="secondary"
            :opacity="0.80"
            blur="2px">
            <b-card-body>
                <validation-observer ref="observer" v-slot="{ passes }">
                <b-form @submit.prevent="passes(onFormSubmit)" novalidate>
                    <b-row>
                        <b-col cols="7">
                            <validation-provider vid="description" v-slot="{ errors }">
                            <speech-area v-shortcut-expand :disabled="isLocked" id="description" v-capitalize name="description" classes="squared-bottom" :errors="errors[0]" v-model="form.description" placeholder="Add history..." :state="errors[0]" rows="11" no-auto-shrink max-rows="20">
                            </speech-area>
                            </validation-provider>
                        </b-col>
                        <b-col>
                            <b-form-group v-if="useICPC" :disabled="isLocked" label-for="icpc-id">
                                <template slot="label">
                                    <span class="text-primary"><i class="fa-solid fa-seal"></i></span> ICPC Codes
                                </template>
                                <MultiICPCField v-model="form.icpc_ids" :disabled="isLocked"/>
                            </b-form-group>
                            <b-form-group v-if="useICD10" :disabled="isLocked" label-for="icd10-id">
                                <template slot="label">
                                    <span class="text-primary"><i class="fa-solid fa-seal"></i></span> ICD-10 Codes
                                </template>
                                <MultiICD10Field v-model="form.icd10_ids" :disabled="isLocked"/>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-button class="mr-1" :disabled="isLocked || !isDirty" type="submit" variant="success"><i class="fa-regular fa-circle-dot"></i> Submit</b-button>
                    <b-button v-if="isLocked" type="button" variant="warning" @click="isLocked = false"><i class="fa-solid fa-unlock"></i> Unlock</b-button>
                    <b-button v-if="!isLocked && !isDirty && !hasErrors" type="button" variant="outline-primary" @click="isLocked = true"><i class="fa-solid fa-lock"></i> Lock</b-button>
                    <b-button v-if="isDirty || hasErrors" type="button" variant="danger" @click="reset"><i class="fa-solid fa-ban"></i> Reset</b-button>
                    <b-button :disabled="noRevisions" type="button" class="float-right" variant="primary" @click="historyRevisions" v-b-tooltip.hover title="Browse Revisions">
                        <i class="fa-solid fa-clock-rotate-left"></i>      
                    </b-button>
                </b-form>
                </validation-observer>
            </b-card-body>
        </b-overlay>

        </b-collapse>
    </b-card>
</template>
<script>
import Vue from 'vue'
import FormExtension from "@/core/components/FormExtension"
import { mapGetters, mapState, mapActions } from "vuex"
import MultiICPCField from '../catalogue/MultiICPCField'
import MultiICD10Field from '../catalogue/MultiICD10Field'
import { useICPC, useICD10 } from '@/shared/clinicCodes'

export default FormExtension.extend({
    name: "history",
    components: {
        MultiICPCField,
        MultiICD10Field
    },
    data() {
        return {
            useICPC,
            useICD10,
            form: new Form({
                description: "",
                icpc_ids: [],
                icd10_ids: [],
                consistency_token: null
            })
        }
    },
    computed: {
        ...mapGetters('health', ['getPatientHistory']),
        ...mapGetters('revisions', ['getRevisionsForHistory']),
        revisions() {
            return this.getRevisionsForHistory()
        },
        history() {
            return this.getPatientHistory()
        },
        noRevisions() {
            return this.revisions.length < 2
        }
    },
    watch: {
        history: {
            handler(val) {
                if (val) {
                    console.log('History loaded from API:', val)
                    console.log('API has icd10_ids?', 'icd10_ids' in val, 'value:', val.icd10_ids)
                    console.log('API has icpc_ids?', 'icpc_ids' in val, 'value:', val.icpc_ids)
                    
                    // Add missing fields before creating Form
                    if (!val.icd10_ids) {
                        val.icd10_ids = []
                    }
                    
                    this.form = Form.create(val)
                    console.log('Form created, form.data():', this.form.data())
                    this.getHistoryRevisions(val.id)
                }else{
                    this.form = Form.create({
                        description: "",
                        icpc_ids: [],
                        icd10_ids: [],
                        consistency_token: null
                    })
                }
            }
        },
        form: {
            deep: true,
            handler(val) {
                console.log('History form watcher triggered')
                console.log('  icpc_ids:', this.form.icpc_ids)
                console.log('  icd10_ids:', this.form.icd10_ids)
                console.log('  form data:', this.form.data())
                this.isDirty = this.form.dirty()
                console.log('  isDirty:', this.isDirty)

                if (this.isDirty) {
                    this.dirty()
                }else{
                    this.validated()
                }
            }
        }
    },
    methods: {
        ...mapActions('health', ['getHistoryForCurrentPatient', 'createPatientHistory', 'updatePatientHistory']),
        ...mapActions('revisions', ['getHistoryRevisions', 'revertHistoryRevision']),
        async loadForPatient(patient) {
            await this.getHistoryForCurrentPatient()
        },
        create() {
            this.createPatientHistory(this.form.data()).then((history) => {
                this.notify("Patient history created", "Patient history was created successfully")
            })
            .catch(errors => {
                if (errors.response) {
                    this.$refs.observer?.setErrors(errors.response.data)
                    this.hasErrors = true
                }
            })
        },
        update() {
            this.updatePatientHistory({id: this.history.id, history: this.form.data() }).then(() => {
                this.notify("Patient history updated", "Patient history was updated successfully")
            })
            .catch(errors => {
                if (errors.response) {
                    this.$refs.observer?.setErrors(errors.response.data)
                    this.hasErrors = true
                }
            })
        },
        onFormSubmit() {
            if (this.history) {
                this.update()
            }else{
                this.create()
            }
        },
        reset() {
            this.form.reset()
            this.$nextTick(() => {
                this.$refs.observer?.reset()
                this.hasErrors = false
                this.validated()
            })
        },
        historyRevisions() {
            this.browseRevisions('General History', this.revisions)
        },
        async reverted(version) {
            await this.revertHistoryRevision({id: this.history.id, version: version})
        }
    }
})
</script>